import React, { useState, useEffect, useCallback } from 'react';
import { MessageCircle, Send, Pencil, Save, X, Loader2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import SalesBotPanel from './SalesBotPanel';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Defaults — used only as an instant first-paint while the GET hits the
// backend. The server seeds the same list into `wa_templates` on first
// request so a fresh DB doesn't show an empty list.
const DEFAULT_TEMPLATES = [
  { id: 1, name: 'אישור הזמנה', message: 'שלום {name}\nהמשלוח שלך הוקם ויגיע עד 2 ימי עסקים\nל{address}\n\nתודה מראש,\nבריאות הגבר' },
  { id: 2, name: 'מזומן - אישור', message: 'נא לשים לב\n{name} היקר!\n\nאנו משלמים 37 ש״ח עבורך על המשלוח מראש.\n\nבסה"כ של: {total} ש"ח\n\nהמשלוח יגיע עד 2 ימי עסקים לכתובת: {address}\n\nאם תסרב לקבל את המשלוח או לא תהיה בכתובת והמשלוח יחזור.\n\nאנחנו נצטרך לגבות ממך 37 ש"ח\n\nלאישור השב: 1\n\nבריאות הגבר' },
  { id: 3, name: 'תשלום ביט', message: '{name} שלום,\n\nתודה על ההזמנה, יש להיכנס ללינק מטה למעבר תשלום נוח בביט\n\n{bitLink}' },
  { id: 4, name: 'מעקב משלוח', message: 'שלום {name},\n\nעדכון לגבי המשלוח שלך:\nמספר מעקב: {tracking}\n\nבריאות הגבר' },
];

const AdminWhatsApp = () => {
  const [templates, setTemplates] = useState(DEFAULT_TEMPLATES);
  const [editingId, setEditingId] = useState(null);
  // Draft holds the in-progress edit so canceling restores the original.
  const [draft, setDraft] = useState({ name: '', message: '' });
  const [savingId, setSavingId] = useState(null);
  const [quickPhone, setQuickPhone] = useState('');
  const [quickMessage, setQuickMessage] = useState('');

  const fetchTemplates = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/wa-templates`);
      if (Array.isArray(r.data) && r.data.length > 0) setTemplates(r.data);
    } catch (_e) { /* graceful — DEFAULT_TEMPLATES remain */ }
  }, []);

  useEffect(() => { fetchTemplates(); }, [fetchTemplates]);

  const startEdit = (t) => {
    setEditingId(t.id);
    setDraft({ name: t.name, message: t.message });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setDraft({ name: '', message: '' });
  };

  const saveEdit = async (t) => {
    if (!draft.message.trim()) {
      toast.error('ההודעה לא יכולה להיות ריקה');
      return;
    }
    setSavingId(t.id);
    try {
      const r = await axios.put(`${API}/admin/wa-templates/${t.id}`, {
        name: draft.name,
        message: draft.message,
      });
      setTemplates(prev => prev.map(x => x.id === t.id ? { ...x, ...r.data } : x));
      toast.success('התבנית נשמרה');
      setEditingId(null);
    } catch (err) {
      toast.error('שגיאה בשמירה: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSavingId(null);
    }
  };

  const sendQuickMessage = () => {
    if (!quickPhone || !quickMessage) { toast.error('נא למלא טלפון והודעה'); return; }
    const phone = `972${quickPhone.replace(/^0/, '').replace(/[-\s]/g, '')}`;
    window.open(`https://wa.me/${phone}?text=${encodeURIComponent(quickMessage)}`, '_blank', 'noopener,noreferrer');
  };

  return (
    <div dir="rtl" data-testid="admin-whatsapp-section">
      <h2 className="text-xl font-black text-[#2C3E2D] mb-4" style={{ fontFamily: 'Heebo, sans-serif' }}>
        <MessageCircle className="w-5 h-5 inline ml-2 text-green-500" />ניהול וואטסאפ
      </h2>

      {/* Sales Bot — AI-driven order taking via WhatsApp. Customers send
          a message, the bot walks them through product → name → address
          → payment → confirm, and creates an order in the dashboard
          automatically. */}
      <SalesBotPanel />

      {/* Quick Send */}
      <div className="bg-white rounded-xl p-4 border border-stone-200 mb-4 mt-6">
        <h3 className="font-bold text-sm mb-3">שליחת הודעה מהירה</h3>
        <div className="flex gap-2 mb-2">
          <input placeholder="מספר טלפון" value={quickPhone} onChange={e => setQuickPhone(e.target.value)} className="border rounded-lg px-3 py-2 text-sm flex-1" dir="ltr" data-testid="wa-quick-phone" />
          <button onClick={sendQuickMessage} className="bg-green-500 hover:bg-green-600 text-white rounded-lg px-4 py-2 text-sm font-bold flex items-center gap-1" data-testid="wa-quick-send">
            <Send className="w-4 h-4" /> שלח
          </button>
        </div>
        <textarea placeholder="הודעה..." value={quickMessage} onChange={e => setQuickMessage(e.target.value)} className="w-full border rounded-lg px-3 py-2 text-sm" rows={3} data-testid="wa-quick-message" />
      </div>

      {/* Templates */}
      <h3 className="font-bold text-sm mb-2 text-stone-600">תבניות הודעות</h3>
      <div className="space-y-2">
        {templates.map(t => {
          const isEditing = editingId === t.id;
          const isSaving = savingId === t.id;
          return (
            <div key={t.id} className="bg-white rounded-xl p-4 border border-stone-200" data-testid={`wa-template-${t.id}`}>
              <div className="flex items-center justify-between mb-2 gap-2">
                {isEditing ? (
                  <input
                    type="text"
                    value={draft.name}
                    onChange={e => setDraft(d => ({ ...d, name: e.target.value }))}
                    className="font-bold text-sm text-stone-700 border-b border-stone-300 focus:border-[#D97706] outline-none flex-1 min-w-0"
                    data-testid={`wa-template-name-input-${t.id}`}
                  />
                ) : (
                  <span className="font-bold text-sm text-stone-700">{t.name}</span>
                )}
                {isEditing ? (
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    <button
                      onClick={() => saveEdit(t)}
                      disabled={isSaving}
                      className="flex items-center gap-1 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white rounded-lg text-xs font-bold"
                      data-testid={`wa-template-save-${t.id}`}
                    >
                      {isSaving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                      שמור
                    </button>
                    <button
                      onClick={cancelEdit}
                      disabled={isSaving}
                      className="flex items-center gap-1 px-3 py-1.5 bg-stone-200 hover:bg-stone-300 disabled:opacity-60 text-stone-700 rounded-lg text-xs font-bold"
                      data-testid={`wa-template-cancel-${t.id}`}
                    >
                      <X className="w-3.5 h-3.5" />
                      בטל
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => startEdit(t)}
                    className="flex items-center gap-1 text-xs text-[#D97706] hover:underline flex-shrink-0"
                    data-testid={`wa-template-edit-${t.id}`}
                  >
                    <Pencil className="w-3.5 h-3.5" />
                    ערוך
                  </button>
                )}
              </div>
              {isEditing ? (
                <textarea
                  value={draft.message}
                  onChange={e => setDraft(d => ({ ...d, message: e.target.value }))}
                  className="w-full border-2 border-[#D97706]/30 focus:border-[#D97706] rounded-lg px-3 py-2 text-sm outline-none"
                  rows={6}
                  data-testid={`wa-template-message-input-${t.id}`}
                />
              ) : (
                <p className="text-xs text-stone-400 whitespace-pre-wrap leading-relaxed">{t.message}</p>
              )}
            </div>
          );
        })}
      </div>

      <div className="mt-4 p-3 bg-stone-50 rounded-xl text-xs text-stone-400">
        <p className="font-bold mb-1">משתנים זמינים:</p>
        <p>{'{name}'} - שם הלקוח | {'{total}'} - סכום | {'{address}'} - כתובת | {'{tracking}'} - מספר מעקב | {'{bitLink}'} - לינק ביט</p>
      </div>
    </div>
  );
};

export default AdminWhatsApp;
